import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Snackbar } from "@mui/material";
import { TaskDetail } from "../../services/taskService";
import { TaskStoreProvider, useTaskStore } from "./TaskContext";
import { QuickAddDefaults, QuickAddDialog } from "./components/QuickAdd";

// Only the team and "today" are needed here — not the task list.
const LIGHT_QUERY = { status: ["DONE" as const], done_days: 0 };

interface NewTaskAnywhereProps {
    open: boolean;
    onClose: () => void;
    defaults?: QuickAddDefaults;
    onCreated?: (task: TaskDetail) => void;
}

/** "+ Task" from the header or the T key, on any page. */
export const NewTaskAnywhere: React.FC<NewTaskAnywhereProps> = (props) => (
    <TaskStoreProvider query={LIGHT_QUERY} pollMs={0}>
        <Inner {...props} />
    </TaskStoreProvider>
);

const Inner: React.FC<NewTaskAnywhereProps> = ({ open, onClose, defaults, onCreated }) => {
    const navigate = useNavigate();
    const { lastToast, clearToast, toast } = useTaskStore();
    return (
        <>
            <QuickAddDialog
                open={open}
                onClose={onClose}
                defaults={defaults}
                onCreated={(task) => {
                    onCreated?.(task);
                    toast({
                        message: "Task added.",
                        severity: "success",
                        action: { label: "Open", onClick: () => navigate(`/tasks?task=${task.task_id}`) },
                    });
                }}
            />
            <Snackbar
                open={!!lastToast}
                autoHideDuration={lastToast?.severity === "error" ? 6000 : 4000}
                onClose={(_, reason) => reason !== "clickaway" && clearToast()}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {lastToast ? (
                    <Alert
                        severity={lastToast.severity || "info"}
                        variant="filled"
                        onClose={clearToast}
                        action={
                            lastToast.action ? (
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        lastToast.action!.onClick();
                                        clearToast();
                                    }}
                                >
                                    {lastToast.action.label}
                                </Button>
                            ) : undefined
                        }
                        sx={{ alignItems: "center" }}
                    >
                        {lastToast.message}
                    </Alert>
                ) : (
                    <span />
                )}
            </Snackbar>
        </>
    );
};

const isTyping = (target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
};

/** T opens "New task" — when not typing and nothing else is open. */
export function useNewTaskShortcut(enabled: boolean, open: () => void) {
    useEffect(() => {
        if (!enabled) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.repeat || isTyping(e.target)) return;
            if (e.key !== "t" && e.key !== "T" && e.code !== "KeyT") return;
            if (document.querySelector(".MuiModal-root")) return;
            e.preventDefault();
            open();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [enabled, open]);
}
